import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Divider,
  Container
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CircleIcon from '@mui/icons-material/Circle';
import PolicyIcon from '@mui/icons-material/Policy';

// ----------------------------------------------------------------------
// DATA STRUCTURE FOR THE HANDBOOK
// ----------------------------------------------------------------------
const handbookData = [
  {
    id: "part-I",
    title: "Part I: Core Safeguarding Policy",
    content: [
      {
        type: "paragraph",
        text: "Ethio Future Tutor is committed to providing a safe, respectful, and inclusive online learning environment for all students, particularly children, women, and vulnerable learners. We operate a zero-tolerance policy toward abuse, harassment, exploitation, or any form of misconduct."
      },
      { type: "heading", text: "Scope and Principles" },
      {
        type: "list",
        items: [
          "This policy applies to teachers, students, parents, guardians, and platform staff.",
          "The best interests of the child are paramount.",
          "All users have the right to dignity, safety, and respect.",
          "Abuse and harassment are preventable through clear rules.",
          "Safeguarding concerns must be taken seriously and acted upon promptly."
        ]
      },
      { type: "heading", text: "Universal Safety Measures" },
      {
        type: "list",
        items: [
          "All sessions are conducted only through the platform; communication outside the platform is not permitted.",
          "Sessions will be recorded for safeguarding and quality assurance purposes. Access logs and session records are securely stored."
        ]
      }
    ]
  },
  {
    id: "part-II",
    title: "Part II: Code of Conduct for Students and Parents",
    content: [
      {
        type: "paragraph",
        text: "This section ensures a safe, respectful, and effective learning environment for users engaging with Ethio Future Tutor."
      },
      { type: "heading", text: "Student Responsibilities" },
      {
        type: "list",
        items: [
          "Treat teachers and platform staff with respect.",
          "Use appropriate language and behavior during sessions.",
          "Attend sessions prepared and on time.",
          "Use the platform only for educational purposes."
        ]
      },
      { type: "heading", text: "Parent Responsibilities (If Applicable)" },
      {
        type: "list",
        items: [
          "Support the student's respectful participation.",
          "Ensure that children use the platform in a safe environment. Provide accurate registration information.",
          "Report any safeguarding concerns immediately."
        ]
      },
      { type: "heading", text: "Prohibited Conduct for Students and Parents" },
      {
        type: "list",
        items: [
          "Using abusive, threatening, or inappropriate language or behavior. Recording or sharing tutoring sessions without permission.",
          "Harassing, intimidating, or falsely accusing teachers or staff. Misusing the platform for non-educational or harmful purposes."
        ]
      }
    ]
  },
  {
    id: "part-III",
    title: "Part III: Tutor Qualifications and Professional Conduct",
    content: [
      {
        type: "paragraph",
        text: "This section establishes professional, ethical, and safeguarding standards for all teachers engaged with Ethio Future Tutor as independent service providers."
      },
      { type: "heading", text: "Minimum Qualifications and Requirements" },
      {
        type: "list",
        items: [
          "Must be a current university student or university graduate with a recognized diploma or degree in a relevant field, maintaining a minimum cumulative GPA of 3.0 or above.",
          "Must provide proof of university enrollment or graduation, along with a valid government-issued National ID.",
          "Must have clear communication skills in the language of instruction (English and Amharic) and demonstrate strong knowledge of the subjects taught.",
          "Must possess a reliable internet connection, a device with a camera and microphone, basic digital literacy, and a quiet, professional environment for sessions.",
          "Must commit to agreed schedules, be punctual, and notify the platform in advance of unavailability."
        ]
      },
      { type: "heading", text: "Professional Duties and Conduct" },
      {
        type: "list",
        items: [
          "Deliver live online tutoring only within approved subject areas and scheduled times.",
          "Support students with homework and exam preparation by guiding them, rather than completing work on their behalf.",
          "Adapt teaching methods to suit different learning levels and styles.",
          "Maintain appropriate professional boundaries with students at all times.",
          "Use respectful, non-discriminatory language regardless of gender, age, religion, ethnicity, disability, or background."
        ]
      },
      { type: "heading", text: "Strictly Prohibited Tutor Conduct" },
      {
        type: "list",
        items: [
          "Sexual harassment, exploitation, grooming, suggestive comments, or inappropriate familiarity.",
          "Verbal abuse, emotional or psychological abuse, threats, discrimination, or hate speech.",
          "Requesting personal information unrelated to learning or sharing personal contact details to move communication off-platform (e.g., phone, social media).",
          "Recording, sharing, or misusing session content without authorization.",
          "Exposing learners to inappropriate content, including sexual, violent, political, or extremist material."
        ]
      }
    ]
  },
  {
    id: "part-IV",
    title: "Part IV: Tutor Financial and Service Agreement",
    content: [
      { type: "heading", text: "Pricing and Commission" },
      {
        type: "list",
        items: [
          "Tutors set their own hourly or session-based fees, with a minimum price floor of 400 ETB per session.",
          "The platform reserves the right to revise the minimum price floor with or without prior notice.",
          "Ethio Future Tutor will deduct a platform commission of 10% from each completed tutoring session, with the remaining balance transferred to the Tutor."
        ]
      },
      { type: "heading", text: "Payment Terms" },
      {
        type: "list",
        items: [
          "Payouts will be processed on a weekly basis via the platform's approved payment methods.",
          "Payments will only be made for sessions successfully completed on the platform.",
          "No payment will be made for sessions terminated due to Tutor misconduct.",
          "Tutors are responsible for providing accurate payment details; the platform is not liable for delays caused by incorrect information or third-party providers."
        ]
      },
      { type: "heading", text: "Confidentiality and Liability" },
      {
        type: "list",
        items: [
          "Tutors must keep all student information confidential and must not store, share, or misuse platform data.",
          "Tutors agree to indemnify Ethio Future Tutor against claims arising from misconduct, breach of this agreement, or violation of applicable laws."
        ]
      }
    ]
  },
  {
    id: "part-V",
    title: "Part V: Reporting, Enforcement, and Legal Compliance",
    content: [
      { type: "heading", text: "Reporting Procedures" },
      {
        type: "list",
        items: [
          "Any user or tutor must immediately report concerns related to harassment, abuse, inappropriate behavior, or safeguarding risks.",
          "Reports should be made through the platform's email: ethiofuturetutor.report@gmail.com.",
          "All reports will be treated confidentially, reviewed promptly, and investigated fairly.",
          "Tutors must cooperate fully with investigations and provide truthful information."
        ]
      },
      { type: "heading", text: "Enforcement and Dispute Resolution" },
      {
        type: "list",
        items: [
          "Depending on severity, actions may include internal investigation, immediate suspension, permanent account termination, and referral to law enforcement.",
          "False or malicious reports may result in account suspension.",
          "Platform access logs, chat records, and video recordings remain the exclusive property of Ethio Future Tutor.",
          "These records serve as the final reference for payment disputes and may be used as evidence during investigations or submitted to courts and law enforcement.",
          "During complaints or investigations, tutor payments may be temporarily withheld until resolution."
        ]
      },
      { type: "heading", text: "Legal Compliance" },
      {
        type: "list",
        items: [
          "This policy aligns with child protection principles, online safety standards, and applicable national laws.",
          "All agreements and policies shall be governed by the laws of Ethiopia.",
          "The platform may update policies, commission rates, or procedures with or without notice."
        ]
      }
    ]
  },
  {
    id: "part-VI",
    title: "Part VI: Declaration and Acceptance",
    content: [
      {
        type: "paragraph",
        text: "Use of the Ethio Future Tutor platform constitutes full acceptance of these policies and agreements."
      },
      {
        type: "paragraph",
        text: "By registering and utilizing the platform, all Students, Parents, and Tutors confirm that they:"
      },
      {
        type: "list",
        items: [
          "Have read and understood the relevant Codes of Conduct, Safeguarding Policies, and Agreements.",
          "Agree to comply fully with the rules, ethical standards, and payment terms.",
          "Accept the disciplinary, financial, and legal consequences for violations."
        ]
      },
      {
        type: "paragraph",
        text: "**Binding Agreement:** By accessing, registering on, or providing and receiving services through the Ethio Future Tutor platform, all users (students, parents, and teachers) acknowledge and confirm that they are fully bound by the aforementioned Comprehensive Policies and Agreements Handbook, as well as any applicable local and international laws."
      }
    ]
  }
];

// ----------------------------------------------------------------------
// MAIN COMPONENT
// ----------------------------------------------------------------------
export default function PolicyHandbook() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // Offset by 80px to account for the sticky top nav on mobile
      const yOffset = -80; 
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    if (isMobile) {
      setMobileOpen(false); // Close drawer on mobile after clicking
    }
  };

  // The sidebar navigation list
  const tocDrawer = (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PolicyIcon color="primary" />
        Policies Menu
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <List disablePadding>
        {handbookData.map((section) => (
          <ListItem disablePadding key={`toc-${section.id}`} sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => scrollToSection(section.id)}
              sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <ListItemText
                primary={section.title.split('[')[0]} // Strip the citation from the menu text for cleanliness
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile Top App Bar */}
      {isMobile && (
        <AppBar position="fixed" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Handbook
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }} // Better open performance on mobile
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {tocDrawer}
      </Drawer>

      <Container maxWidth="lg" sx={{ pt: isMobile ? 10 : 6, pb: 8 }}>
        <Box sx={{ display: 'flex', gap: 4 }}>
          
          {/* Desktop Sticky Sidebar */}
          {!isMobile && (
            <Box
              sx={{
                width: 300,
                flexShrink: 0,
                position: 'sticky',
                top: 32, // Distance from top of screen
                height: 'fit-content',
                maxHeight: 'calc(100vh - 64px)',
                overflowY: 'auto',
                borderRight: 1,
                borderColor: 'divider',
                pr: 2
              }}
            >
              {tocDrawer}
            </Box>
          )}

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, maxWidth: 800 }}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                Comprehensive Policies and Agreements Handbook
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Ethio Future Tutor
              </Typography>
            </Box>

            {/* Loop through data and render standard text blocks */}
            {handbookData.map((section) => (
              <Box id={section.id} key={section.id} sx={{ mb: 6, scrollMarginTop: isMobile ? '80px' : '40px' }}>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                  {section.title}
                </Typography>

                {section.content.map((block, index) => {
                  if (block.type === 'paragraph') {
                    // Check if paragraph requires bold formatting
                    const isBold = block.text.includes('**');
                    const cleanText = block.text.replace(/\*\*/g, '');
                    return (
                      <Typography
                        key={index}
                        variant="body1"
                        sx={{ mb: 3, lineHeight: 1.7, fontWeight: isBold ? 600 : 400 }}
                      >
                        {cleanText}
                      </Typography>
                    );
                  }

                  if (block.type === 'heading') {
                    return (
                      <Typography key={index} variant="h6" component="h3" sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
                        {block.text}
                      </Typography>
                    );
                  }

                  if (block.type === 'list') {
                    return (
                      <List key={index} disablePadding sx={{ mb: 3 }}>
                        {block.items.map((item, i) => (
                          <ListItem key={i} alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28, mt: 1 }}>
                              <CircleIcon sx={{ fontSize: 8, color: 'text.secondary' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={item}
                              primaryTypographyProps={{ variant: 'body1', lineHeight: 1.7, color: 'text.primary' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    );
                  }
                  return null;
                })}
              </Box>
            ))}
          </Box>

        </Box>
      </Container>
    </Box>
  );
}